import React from 'react';
import { useOrganizationClientRecords } from '../../../../../../../hooks/api/useOrganizationClientRecords';
import { OrganizationTypes } from '../../../../../../../primary-record-types';
import Stack, { ContentStack } from '../../../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import { clientRecordStatusTypeGuard } from '../../../../../../../utils/clientRecordsUtil';
import { getIntl } from '../../../../../../../utils/intl';
import InfoIcon from '../../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../../v2BaseComponents/Prompt';
import getName from '../../../../../../../utils/getName';

const intl = getIntl('unarchive-client-prompt-content', {
    unarchiveClient: 'Unarchive Client',
    unarchiveClientPrompt: 'Are you sure you want to unarchive {clientName}?',
    thisClient: 'this client',
    archive: 'Unarchive',
    cancel: 'Cancel',
});

export type UnArchiveClientPromptContentOwnProps = {
    onClose: () => void;
    organizationClientRecordId: string;
    onUnarchive: (organizationClientRecordId: string) => void;
};

export type UnArchiveClientPromptContentProps = UnArchiveClientPromptContentOwnProps;

const Content: React.FC<{
    clientRecord: OrganizationTypes.OrganizationClientRecordForStatus<'archived'>;
    onReEngageClientRecord: () => void;
    onClose: () => void;
}> = ({ clientRecord, onReEngageClientRecord, onClose }) => (
    <ContentStack>
        <InfoIcon
            alertIconProps={{
                name: 'UserPlus',
                variant: 'info',
            }}
            title={intl('unarchiveClient')}
            subText={intl('unarchiveClientPrompt', {
                clientName: getName(clientRecord.clientDetails) || intl('thisClient'),
            })}
        />
        <PromptActions
            actions={[
                {
                    title: intl('archive'),
                    onPress: onReEngageClientRecord,
                    icon: 'UserPlus',
                    variant: 'primary',
                    testID: 'confirm-unarchive-client',
                },
                {
                    title: intl('cancel'),
                    onPress: onClose,
                    variant: 'secondary',
                    icon: 'XCircle',
                    testID: 'cancel-unarchive-client',
                },
            ]}
        />
    </ContentStack>
);

const UnArchiveClientPromptContent: React.FC<UnArchiveClientPromptContentProps> = ({
    onClose,
    organizationClientRecordId,
    onUnarchive,
}) => {
    const { isLoadingClientRecord, clientRecord, isArchivingClientRecord } = useOrganizationClientRecords({
        organizationClientRecordId,
    });

    const isLoading = isLoadingClientRecord || !clientRecord || isArchivingClientRecord;

    return (
        <Stack vertical fullWidth style={{ minHeight: 200 }}>
            <AnimateLoadedContent isLoading={isLoading}>
                {() =>
                    clientRecord && clientRecordStatusTypeGuard.archived(clientRecord) ? (
                        <Content
                            clientRecord={clientRecord}
                            onReEngageClientRecord={() => onUnarchive(organizationClientRecordId)}
                            onClose={onClose}
                        />
                    ) : null
                }
            </AnimateLoadedContent>
        </Stack>
    );
};

export default React.memo(UnArchiveClientPromptContent);
